/**
 * Music system for Counter Pump.
 *
 * Synthesizes all music using the Web Audio API — no external audio files.
 * Provides menu ambience, round stings, bomb tension music, and MVP fanfare.
 *
 * Volume is stored independently from SFX in localStorage and supports
 * ducking (temporarily reducing volume when the announcer plays).
 */

// ── Constants ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'counter_pump_music_volume';
const DEFAULT_VOLUME = 0.3;
const DUCK_LEVEL = 0.15; // multiplier applied during ducking
const FADE_TIME = 0.5; // seconds for fade in/out

// Musical note frequencies (Hz) — equal temperament, A4 = 440
const NOTE = {
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.00, A3: 220.00, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99,
} as const;

// ── Track identifiers ───────────────────────────────────────────────────────

export type MusicTrack =
  | 'menu'
  | 'round_start'
  | 'round_end_win'
  | 'round_end_loss'
  | 'bomb_tension'
  | 'ten_second_warning'
  | 'mvp';

export type BombTensionPhase = 'early' | 'mid' | 'late';

export interface AdaptiveMusicState {
  bombTimeRemaining?: number;
  bombTotalTime?: number;
  localAlive?: number;
  enemyAlive?: number;
  defuseActive?: boolean;
  recentKill?: boolean;
  localTeamEconomy?: number;
  enemyTeamEconomy?: number;
}

export interface AdaptiveMusicParams {
  phase: BombTensionPhase;
  tempoBpm: number;
  intensity: number;
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function computeAdaptiveMusicParams(state: AdaptiveMusicState): AdaptiveMusicParams {
  const total = state.bombTotalTime && state.bombTotalTime > 0 ? state.bombTotalTime : 40;
  const remaining = typeof state.bombTimeRemaining === 'number'
    ? clamp(state.bombTimeRemaining, 0, total)
    : total;
  const progress = 1 - remaining / total;
  const phase: BombTensionPhase = remaining <= 10 ? 'late' : remaining <= total * 0.625 ? 'mid' : 'early';

  let tempoBpm = phase === 'early' ? 82 : phase === 'mid' ? 122 : 168;
  let intensity = phase === 'early' ? 0.35 : phase === 'mid' ? 0.62 : 0.9;

  tempoBpm += progress * 10;
  intensity += progress * 0.05;

  if (state.defuseActive) {
    tempoBpm += 20;
    intensity += 0.14;
  }
  if (state.recentKill) {
    tempoBpm += 8;
    intensity += 0.05;
  }
  if (
    typeof state.localAlive === 'number' &&
    typeof state.enemyAlive === 'number' &&
    state.localAlive > 0 &&
    state.enemyAlive > state.localAlive
  ) {
    const disadvantage = Math.min(3, state.enemyAlive - state.localAlive);
    tempoBpm += disadvantage * 6;
    intensity += disadvantage * 0.05;
  }
  if (
    typeof state.localTeamEconomy === 'number' &&
    typeof state.enemyTeamEconomy === 'number' &&
    state.localTeamEconomy < 2000 &&
    state.enemyTeamEconomy > state.localTeamEconomy + 1000
  ) {
    tempoBpm += 6;
    intensity += 0.06;
  }

  return {
    phase,
    tempoBpm: Math.round(clamp(tempoBpm, 60, 190)),
    intensity: clamp01(intensity),
  };
}

// ── Active nodes container ──────────────────────────────────────────────────
// Tracks all Web Audio nodes for the currently playing piece so they can be
// stopped and disconnected cleanly.

interface ActiveNodes {
  oscillators: OscillatorNode[];
  gains: GainNode[];
  filters: BiquadFilterNode[];
  timers: ReturnType<typeof setTimeout>[];
  animFrameId: number | null;
}

function emptyActiveNodes(): ActiveNodes {
  return { oscillators: [], gains: [], filters: [], timers: [], animFrameId: null };
}

// ── MusicSystem ─────────────────────────────────────────────────────────────

export class MusicSystem {
  private ctx: AudioContext;
  private masterGain: GainNode;
  private volume: number;
  private currentTrack: MusicTrack | null = null;
  private ducked = false;
  private active: ActiveNodes = emptyActiveNodes();
  private disposed = false;
  /** Incremented on every stopCurrent() to invalidate stale timer callbacks. */
  private generation = 0;
  private adaptiveState: AdaptiveMusicState = { bombTimeRemaining: 40, bombTotalTime: 40 };
  private adaptiveParams: AdaptiveMusicParams = computeAdaptiveMusicParams(this.adaptiveState);

  constructor() {
    // Load persisted volume or use default
    this.volume = this.loadVolume();

    // Create audio context + master gain
    const AudioCtx = (globalThis as any).AudioContext ?? (globalThis as any).webkitAudioContext;
    this.ctx = new AudioCtx();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = this.volume;
    this.masterGain.connect(this.ctx.destination);
  }

  // ── Public API: state queries ─────────────────────────────────────────

  /** True if any track is currently active. */
  isPlaying(): boolean {
    return this.currentTrack !== null;
  }

  /** The identifier of the currently playing track, or null. */
  getCurrentTrack(): MusicTrack | null {
    return this.currentTrack;
  }

  /** Current music volume (0-1). */
  getVolume(): number {
    return this.volume;
  }

  /**
   * A-M-005: expose the MusicSystem's AudioContext as the canonical
   * shared WebAudio context. Other audio subsystems (SpatialAudioSystem)
   * reuse this instead of constructing their own — otherwise mobile
   * Safari ends up with multiple suspended contexts, each consuming its
   * own ~1 MB scratch buffer and competing for the autoplay-policy
   * unlock gesture.
   */
  getAudioContext(): AudioContext {
    return this.ctx;
  }

  /**
   * Per-map music theme. de_outpost_v6 shifts the synthesized tracks down
   * a perfect 4th into a darker industrial register. Unknown maps use the
   * base C-major tuning.
   */
  setMapTheme(mapId: string): void {
    if (mapId === 'de_outpost_v6') {
      this.mapPitchShift = Math.pow(2, -5 / 12);
    } else {
      this.mapPitchShift = 1.0;
    }
    this.currentMapId = mapId;
  }

  /** Returns the active per-map pitch ratio (1.0 == default tuning). */
  getMapPitchShift(): number {
    return this.mapPitchShift;
  }

  /** Returns the active map id last passed to setMapTheme, or null. */
  getMapId(): string | null {
    return this.currentMapId;
  }

  private currentMapId: string | null = null;
  private mapPitchShift = 1.0;

  /** Whether the music is currently ducked for an announcer. */
  isDucked(): boolean {
    return this.ducked;
  }

  updateAdaptiveState(next: Partial<AdaptiveMusicState>): void {
    this.adaptiveState = { ...this.adaptiveState, ...next };
    this.adaptiveParams = computeAdaptiveMusicParams(this.adaptiveState);
  }

  getAdaptiveTempoBpm(): number {
    return this.adaptiveParams.tempoBpm;
  }

  getAdaptiveIntensity(): number {
    return this.adaptiveParams.intensity;
  }

  // ── Public API: volume ────────────────────────────────────────────────

  /** Set music volume (0-1). Clamped. Persisted to localStorage. */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyVolume();
    this.saveVolume();
  }

  // ── Public API: ducking ───────────────────────────────────────────────

  /** Temporarily reduce music volume while an announcer or important SFX plays. */
  duckForAnnouncer(): void {
    this.ducked = true;
    this.applyVolume();
  }

  /** Restore full music volume after ducking. */
  unduck(): void {
    this.ducked = false;
    this.applyVolume();
  }

  // ── Public API: playback ──────────────────────────────────────────────

  /**
   * Resume the underlying AudioContext. Browsers (notably mobile Safari)
   * start the context suspended until a user gesture is processed, which
   * leaves `currentTime` frozen and scheduled nodes silently piling up.
   * Call this on first gesture (e.g. menu "Play" click) and it's safe to
   * call repeatedly.
   */
  resume(): Promise<void> {
    try {
      if (this.ctx.state === 'suspended') {
        return this.ctx.resume();
      }
    } catch { /* context closed */ }
    return Promise.resolve();
  }

  private ensureRunning(): void {
    try {
      if (this.ctx.state === 'suspended') {
        // Fire-and-forget — if the call is outside a user gesture it will
        // be rejected, but any later gesture-triggered play will succeed.
        this.ctx.resume().catch(() => { /* ignore */ });
      }
    } catch { /* context closed */ }
  }

  /** Play the main-menu ambient drone. Loops until stopMenuMusic(). */
  playMenuMusic(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'menu';
    this.synthMenuAmbient();
  }

  /** Stop the main-menu ambient. */
  stopMenuMusic(): void {
    if (this.currentTrack === 'menu') {
      this.stopCurrent();
    }
  }

  /** Short ascending arpeggio (~1.5 s) at round start / freezetime. */
  playRoundStart(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'round_start';
    this.synthRoundStart();
  }

  /** Major chord resolution (bright, ~2 s). */
  playRoundEndWin(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'round_end_win';
    this.synthRoundEndWin();
  }

  /** Minor chord descent (dark, ~2 s). */
  playRoundEndLoss(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'round_end_loss';
    this.synthRoundEndLoss();
  }

  /** Escalating pulse that accelerates over ~40 s. */
  playBombPlantedTension(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'bomb_tension';
    this.updateAdaptiveState({ bombTimeRemaining: this.adaptiveState.bombTimeRemaining ?? 40, bombTotalTime: 40 });
    this.synthBombTension();
  }

  /** Stop the bomb-planted tension music (on defuse or detonation). */
  stopBombTension(): void {
    if (this.currentTrack === 'bomb_tension' || this.currentTrack === 'ten_second_warning') {
      this.stopCurrent();
    }
  }

  /** Fast urgent beeping layered over tension (~10 s). */
  playTenSecondWarning(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'ten_second_warning';
    this.synthTenSecondWarning();
  }

  /** Triumphant fanfare (~3 s). */
  playMVP(): void {
    this.stopCurrent();
    this.ensureRunning();
    this.currentTrack = 'mvp';
    this.synthMVP();
  }

  /** Stop any currently playing track. */
  stopAll(): void {
    this.stopCurrent();
  }

  /** Release all resources. Safe to call multiple times. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopCurrent();
    try { this.masterGain.disconnect(); } catch { /* already disconnected */ }
    try { this.ctx.close(); } catch { /* already closed */ }
  }

  // ── Internal: stop / cleanup ──────────────────────────────────────────

  private stopCurrent(): void {
    // Bump generation so any in-flight timer callbacks become no-ops
    this.generation++;

    const { oscillators, gains, filters, timers, animFrameId } = this.active;

    // Clear scheduled timers
    for (const t of timers) clearTimeout(t);

    // Cancel animation frame
    if (animFrameId !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(animFrameId);
    }

    // Stop oscillators gracefully
    for (const osc of oscillators) {
      try { osc.stop(); } catch { /* may already be stopped */ }
      try { osc.disconnect(); } catch { /* no-op */ }
    }

    // Disconnect gains and filters
    for (const g of gains) { try { g.disconnect(); } catch { /* no-op */ } }
    for (const f of filters) { try { f.disconnect(); } catch { /* no-op */ } }

    this.active = emptyActiveNodes();
    this.currentTrack = null;
  }

  // ── Internal: volume helpers ──────────────────────────────────────────

  private applyVolume(): void {
    const effective = this.ducked ? this.volume * DUCK_LEVEL : this.volume;
    try {
      this.masterGain.gain.setValueAtTime(effective, this.ctx.currentTime);
    } catch {
      this.masterGain.gain.value = effective;
    }
  }

  private loadVolume(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw !== null) {
        const v = parseFloat(raw);
        if (Number.isFinite(v)) return Math.max(0, Math.min(1, v));
      }
    } catch { /* unavailable */ }
    return DEFAULT_VOLUME;
  }

  private saveVolume(): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(this.volume));
    } catch { /* unavailable */ }
  }

  // ── Internal: helper to schedule auto-stop after a duration ───────────

  private scheduleAutoStop(durationSec: number): void {
    const timer = setTimeout(() => {
      this.stopCurrent();
    }, durationSec * 1000);
    this.active.timers.push(timer);
  }

  // ── Internal: create nodes + register them for cleanup ────────────────

  private osc(type: OscillatorType, freq: number): OscillatorNode {
    const o = this.ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    this.active.oscillators.push(o);
    return o;
  }

  private gain(value: number): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = value;
    this.active.gains.push(g);
    return g;
  }

  private filter(type: BiquadFilterType, freq: number, q: number): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    this.active.filters.push(f);
    return f;
  }

  // ── Synth: Main Menu Ambient ──────────────────────────────────────────
  // Low drone pad: 2 detuned sawtooth oscillators, low-pass filtered,
  // slow filter sweep. Loops via recursive scheduling.

  private synthMenuAmbient(): void {
    const now = this.ctx.currentTime;

    // Two detuned oscillators for thick pad
    const osc1 = this.osc('sawtooth', NOTE.C3);
    osc1.detune.value = -7;
    const osc2 = this.osc('sawtooth', NOTE.C3);
    osc2.detune.value = 7;

    // Sub bass for warmth
    const sub = this.osc('sine', NOTE.C3 / 2); // C2

    // Gain envelopes
    const padGain = this.gain(0);
    const subGain = this.gain(0);

    // Low-pass filter with slow sweep
    const lp = this.filter('lowpass', 200, 2);

    // Fade in over FADE_TIME
    padGain.gain.setValueAtTime(0, now);
    padGain.gain.linearRampToValueAtTime(0.12, now + FADE_TIME);
    subGain.gain.setValueAtTime(0, now);
    subGain.gain.linearRampToValueAtTime(0.08, now + FADE_TIME);

    // Slow filter sweep: 200 → 600 → 200 Hz over 8 seconds, repeating
    const sweepCycle = 8;
    lp.frequency.setValueAtTime(200, now);
    lp.frequency.linearRampToValueAtTime(600, now + sweepCycle / 2);
    lp.frequency.linearRampToValueAtTime(200, now + sweepCycle);

    // Connect: osc → padGain → lp → masterGain
    osc1.connect(padGain);
    osc2.connect(padGain);
    padGain.connect(lp);
    lp.connect(this.masterGain);

    sub.connect(subGain);
    subGain.connect(this.masterGain);

    osc1.start(now);
    osc2.start(now);
    sub.start(now);

    // Reschedule filter sweep every cycle — capture generation to detect staleness
    const gen = this.generation;
    const rescheduleSweep = () => {
      if (this.generation !== gen || this.currentTrack !== 'menu') return;
      const t = this.ctx.currentTime;
      try {
        lp.frequency.cancelScheduledValues(t);
        lp.frequency.setValueAtTime(200, t);
        lp.frequency.linearRampToValueAtTime(600, t + sweepCycle / 2);
        lp.frequency.linearRampToValueAtTime(200, t + sweepCycle);
      } catch { /* context may be closed */ }
      const timer = setTimeout(rescheduleSweep, sweepCycle * 1000);
      this.active.timers.push(timer);
    };
    const timer = setTimeout(rescheduleSweep, sweepCycle * 1000);
    this.active.timers.push(timer);
  }

  // ── Synth: Round Start Sting ──────────────────────────────────────────
  // Ascending 4-note arpeggio, ~1.5 s total.

  private synthRoundStart(): void {
    const now = this.ctx.currentTime;
    const shift = this.mapPitchShift;
    const notes = [NOTE.C4 * shift, NOTE.E4 * shift, NOTE.G4 * shift, NOTE.C5 * shift];
    const noteDur = 0.35;
    const totalDur = notes.length * noteDur + 0.2;

    for (let i = 0; i < notes.length; i++) {
      const o = this.osc('triangle', notes[i]);
      const g = this.gain(0);
      const start = now + i * noteDur;

      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(0.25, start + 0.02);
      g.gain.linearRampToValueAtTime(0.2, start + noteDur * 0.6);
      g.gain.linearRampToValueAtTime(0, start + noteDur);

      o.connect(g);
      g.connect(this.masterGain);
      o.start(start);
      o.stop(start + noteDur + 0.01);
    }

    this.scheduleAutoStop(totalDur);
  }

  // ── Synth: Round End Win ──────────────────────────────────────────────
  // Bright major chord resolution (C major), ~2 s.

  private synthRoundEndWin(): void {
    const now = this.ctx.currentTime;
    const chord = [NOTE.C4, NOTE.E4, NOTE.G4, NOTE.C5];
    const duration = 2;

    for (const freq of chord) {
      const o = this.osc('triangle', freq);
      const g = this.gain(0);

      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.18, now + 0.05);
      g.gain.linearRampToValueAtTime(0.15, now + duration * 0.6);
      g.gain.linearRampToValueAtTime(0, now + duration);

      o.connect(g);
      g.connect(this.masterGain);
      o.start(now);
      o.stop(now + duration + 0.01);
    }

    // Add a bright shimmer
    const shimmer = this.osc('sine', NOTE.E5);
    const sg = this.gain(0);
    sg.gain.setValueAtTime(0, now);
    sg.gain.linearRampToValueAtTime(0.08, now + 0.1);
    sg.gain.linearRampToValueAtTime(0, now + duration);
    shimmer.connect(sg);
    sg.connect(this.masterGain);
    shimmer.start(now);
    shimmer.stop(now + duration + 0.01);

    this.scheduleAutoStop(duration + 0.1);
  }

  // ── Synth: Round End Loss ─────────────────────────────────────────────
  // Dark minor chord descent, ~2 s.

  private synthRoundEndLoss(): void {
    const now = this.ctx.currentTime;
    // C minor descending: C4 → Eb3 (≈ 155.56 Hz), G3, C3
    const chord = [
      { freq: NOTE.C4, end: 155.56 },  // C4 → Eb3
      { freq: NOTE.G3, end: NOTE.F3 },  // G3 → F3
      { freq: NOTE.C3, end: 110 },       // C3 → A2
    ];
    const duration = 2;

    for (const { freq, end } of chord) {
      const o = this.osc('sawtooth', freq);
      const g = this.gain(0);
      const lp = this.filter('lowpass', 800, 1);

      o.frequency.setValueAtTime(freq, now);
      o.frequency.linearRampToValueAtTime(end, now + duration);

      g.gain.setValueAtTime(0, now);
      g.gain.linearRampToValueAtTime(0.12, now + 0.05);
      g.gain.linearRampToValueAtTime(0.1, now + duration * 0.5);
      g.gain.linearRampToValueAtTime(0, now + duration);

      // Darken filter over time
      lp.frequency.setValueAtTime(800, now);
      lp.frequency.linearRampToValueAtTime(200, now + duration);

      o.connect(g);
      g.connect(lp);
      lp.connect(this.masterGain);
      o.start(now);
      o.stop(now + duration + 0.01);
    }

    this.scheduleAutoStop(duration + 0.1);
  }

  // ── Synth: Bomb Planted Tension ───────────────────────────────────────
  // Escalating pulse driven by adaptive state from bomb timer, alive counts,
  // defuse status, recent kills, and economy pressure.
  // Low bass pulse + ticking high frequency.

  private synthBombTension(): void {
    const now = this.ctx.currentTime;
    // Bass pulse oscillator — runs continuously, we modulate its gain
    const bassOsc = this.osc('sine', NOTE.C3);
    const bassGain = this.gain(0.15);
    const bassFilter = this.filter('lowpass', 300, 2);

    bassOsc.connect(bassGain);
    bassGain.connect(bassFilter);
    bassFilter.connect(this.masterGain);
    bassOsc.start(now);

    // High tick oscillator — same approach
    const tickOsc = this.osc('square', 1200);
    const tickGain = this.gain(0);
    tickOsc.connect(tickGain);
    tickGain.connect(this.masterGain);
    tickOsc.start(now);

    // Schedule escalating pulses using setTimeout loop — capture generation to detect staleness
    const gen = this.generation;
    const scheduleNext = () => {
      if (this.generation !== gen || this.currentTrack !== 'bomb_tension') return;

      const remaining = this.adaptiveState.bombTimeRemaining;
      if (typeof remaining === 'number' && remaining <= 0) {
        this.stopCurrent();
        return;
      }

      const { tempoBpm, intensity } = this.adaptiveParams;
      const beatInterval = 60 / tempoBpm;

      // Pulse the bass gain
      const t = this.ctx.currentTime;
      try {
        bassGain.gain.cancelScheduledValues(t);
        bassGain.gain.setValueAtTime(0.12 + intensity * 0.25, t);
        bassGain.gain.linearRampToValueAtTime(0.05, t + beatInterval * 0.8);
      } catch { /* context may be closed */ }

      // Tick
      try {
        tickGain.gain.cancelScheduledValues(t);
        tickGain.gain.setValueAtTime(0.04 + intensity * 0.1, t);
        tickGain.gain.linearRampToValueAtTime(0, t + 0.03);
      } catch { /* context may be closed */ }

      const timer = setTimeout(scheduleNext, beatInterval * 1000);
      this.active.timers.push(timer);
    };

    scheduleNext();
  }

  // ── Synth: 10-Second Warning ──────────────────────────────────────────
  // Fast urgent beeping over tension, ~10 s.

  private synthTenSecondWarning(): void {
    const now = this.ctx.currentTime;
    const duration = 10;
    const beepRate = 8; // beeps per second

    // Base tension drone
    const droneOsc = this.osc('sawtooth', NOTE.C3);
    const droneGain = this.gain(0.1);
    const droneLp = this.filter('lowpass', 400, 2);
    droneOsc.connect(droneGain);
    droneGain.connect(droneLp);
    droneLp.connect(this.masterGain);
    droneOsc.start(now);

    // Urgent beep oscillator
    const beepOsc = this.osc('square', 1800);
    const beepGain = this.gain(0);
    beepOsc.connect(beepGain);
    beepGain.connect(this.masterGain);
    beepOsc.start(now);

    // Schedule rapid beeps
    const beepInterval = 1 / beepRate;
    const numBeeps = Math.floor(duration * beepRate);
    for (let i = 0; i < numBeeps; i++) {
      const t = now + i * beepInterval;
      const urgency = i / numBeeps; // 0→1 escalation
      const vol = 0.08 + urgency * 0.12;

      beepGain.gain.setValueAtTime(vol, t);
      beepGain.gain.linearRampToValueAtTime(0, t + beepInterval * 0.4);
    }

    // Increase drone intensity over time
    droneGain.gain.setValueAtTime(0.1, now);
    droneGain.gain.linearRampToValueAtTime(0.25, now + duration);
    droneLp.frequency.setValueAtTime(400, now);
    droneLp.frequency.linearRampToValueAtTime(800, now + duration);

    this.scheduleAutoStop(duration + 0.1);
  }

  // ── Synth: MVP Sting ──────────────────────────────────────────────────
  // Triumphant fanfare, ~3 s.

  private synthMVP(): void {
    const now = this.ctx.currentTime;
    const duration = 3;

    // Fanfare: ascending power chord hits
    const hits = [
      { notes: [NOTE.C4, NOTE.G4], time: 0, dur: 0.6 },
      { notes: [NOTE.E4, NOTE.B4], time: 0.4, dur: 0.6 },
      { notes: [NOTE.G4, NOTE.D5], time: 0.8, dur: 0.6 },
      // Final triumphant hold
      { notes: [NOTE.C5, NOTE.E5, NOTE.G5], time: 1.3, dur: 1.5 },
    ];

    for (const hit of hits) {
      for (const freq of hit.notes) {
        const o = this.osc('triangle', freq);
        const g = this.gain(0);
        const start = now + hit.time;

        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(0.2, start + 0.03);
        g.gain.linearRampToValueAtTime(0.15, start + hit.dur * 0.5);
        g.gain.linearRampToValueAtTime(0, start + hit.dur);

        o.connect(g);
        g.connect(this.masterGain);
        o.start(start);
        o.stop(start + hit.dur + 0.01);
      }
    }

    // Add a bright harmonic shimmer on the final chord
    const shimmer = this.osc('sine', NOTE.C5 * 2); // C6
    const sg = this.gain(0);
    const shimmerStart = now + 1.3;
    sg.gain.setValueAtTime(0, shimmerStart);
    sg.gain.linearRampToValueAtTime(0.05, shimmerStart + 0.1);
    sg.gain.linearRampToValueAtTime(0, shimmerStart + 1.5);
    shimmer.connect(sg);
    sg.connect(this.masterGain);
    shimmer.start(shimmerStart);
    shimmer.stop(shimmerStart + 1.6);

    this.scheduleAutoStop(duration + 0.1);
  }
}

/** Lazy singleton music system instance — deferred to avoid AudioContext in test envs. */
let _musicSystem: MusicSystem | null = null;
export const musicSystem: MusicSystem = new Proxy({} as MusicSystem, {
  get(_target, prop) {
    if (!_musicSystem) _musicSystem = new MusicSystem();
    return (_musicSystem as any)[prop];
  },
});
